/**
 * ApiStorageProvider - API-backed implementation of CommentStorageProvider.
 * All mutations call the backend first, then refresh from backend on success.
 * Optionally supports real-time collaboration via Yjs.
 */
import {
  Comment,
  CommentReply,
  CommentStatus,
  CreateCommentData,
  CreateReplyData,
} from "types/comments.types"
import { BaseCommentStorageProvider } from "./CommentStorageProvider"
import { AxiosResponse } from "axios"
import { CommentsStorageData } from "types/comments.types"
import * as Y from "yjs"
import { WebsocketProvider } from "y-websocket"

/**
 * Type for the API functions passed from useCommentsApi hook
 */
export interface CommentsApiFunctions {
  getCommentsById: (
    id: string,
    owner: string
  ) => Promise<AxiosResponse<CommentsStorageData>>
  addCommentById: (
    id: string,
    owner: string,
    comment: Comment
  ) => Promise<AxiosResponse<{ id: string }>>
  updateCommentById: (
    id: string,
    owner: string,
    commentId: string,
    updates: Partial<Comment>
  ) => Promise<AxiosResponse<{ success: boolean }>>
  deleteCommentById: (
    id: string,
    owner: string,
    commentId: string,
    deletionData: { deletedBy: string; deletedByName: string; reason: string }
  ) => Promise<AxiosResponse<{ success: boolean }>>
  resolveCommentById: (
    id: string,
    owner: string,
    commentId: string,
    resolvedBy: string,
    resolvedByName: string
  ) => Promise<AxiosResponse<{ success: boolean }>>
  addReplyById: (
    id: string,
    owner: string,
    commentId: string,
    reply: CommentReply
  ) => Promise<AxiosResponse<{ id: string }>>
  deleteReplyById: (
    id: string,
    owner: string,
    commentId: string,
    replyId: string
  ) => Promise<AxiosResponse<{ success: boolean }>>
}

/**
 * Configuration for ApiStorageProvider
 */
export interface ApiStorageProviderConfig {
  api: CommentsApiFunctions
  /** Optional collaboration config for real-time sync */
  collaboration?: {
    websocketUrl: string
    projectId: string
  }
}

/**
 * API-backed implementation of CommentStorageProvider.
 * All mutations call the backend first, then refresh data on success.
 * When collaboration is enabled, also syncs changes via Yjs for real-time updates.
 */
export class ApiStorageProvider extends BaseCommentStorageProvider {
  private api: CommentsApiFunctions

  // Yjs collaboration state
  private ydoc: Y.Doc | null = null
  private provider: WebsocketProvider | null = null
  private commentsMap: Y.Map<Comment> | null = null
  private isProcessingRemoteChange = false
  private collaborationConfig: ApiStorageProviderConfig["collaboration"]

  constructor(config: ApiStorageProviderConfig) {
    super()
    this.api = config.api
    this.collaborationConfig = config.collaboration
  }

  async initialize(
    projectId: string,
    variantName: string = "",
    owner: string = ""
  ): Promise<void> {
    this.projectId = projectId
    this.variantName = variantName
    this.owner = owner

    try {
      await this.refreshFromBackend()

      // Initialize Yjs if collaboration is enabled
      if (this.collaborationConfig) {
        this.initializeYjs()
      }

      this.ready = true
      this.notifyConnectionListeners(true)
    } catch (error) {
      console.error("Failed to fetch comments from API:", error)
      this.comments = new Map()
      this.auditLog = []
      this.ready = true
      this.notifyConnectionListeners(true)
      this.notifyListeners()
    }
  }

  /**
   * Initialize Yjs collaboration for real-time sync
   */
  private initializeYjs(): void {
    if (!this.collaborationConfig) return

    this.ydoc = new Y.Doc()
    this.commentsMap = this.ydoc.getMap<Comment>("comments")

    // Connect to collaboration server with room name: "{projectId}-comments"
    this.provider = new WebsocketProvider(
      this.collaborationConfig.websocketUrl,
      `${this.collaborationConfig.projectId}-comments`,
      this.ydoc
    )

    // Sync current comments to Yjs (so other clients can receive them)
    this.ydoc.transact(() => {
      this.comments.forEach((comment, id) => {
        this.commentsMap?.set(id, comment)
      })
    }, this.ydoc)

    // Observe remote changes from other clients
    this.commentsMap.observe((_event, transaction) => {
      // Only process changes from other clients (not our own)
      if (transaction.origin !== this.ydoc) {
        this.syncFromYjs()
      }
    })

    // Handle connection status
    this.provider.on("status", ({ status }: { status: string }) => {
      this.notifyConnectionListeners(status === "connected")
    })
  }

  /**
   * Sync comments from Yjs to local state (for remote changes)
   */
  private syncFromYjs(): void {
    if (!this.commentsMap) return

    this.isProcessingRemoteChange = true

    // Build new comments map from Yjs
    const newComments = new Map<string, Comment>()
    this.commentsMap.forEach((comment, id) => {
      newComments.set(id, comment)
    })

    // Update local state
    this.comments = newComments
    this.notifyListeners()

    this.isProcessingRemoteChange = false
  }

  /**
   * Sync a comment change to Yjs (for local changes)
   */
  private syncToYjs(commentId: string, comment: Comment | null): void {
    if (!this.commentsMap || this.isProcessingRemoteChange) return

    this.ydoc?.transact(() => {
      if (comment) {
        this.commentsMap?.set(commentId, comment)
      } else {
        this.commentsMap?.delete(commentId)
      }
    }, this.ydoc) // Mark as local change with origin
  }

  /**
   * Refresh comments from backend
   */
  private async refreshFromBackend(): Promise<void> {
    const response = await this.api.getCommentsById(this.projectId, this.owner)
    if (response.data) {
      this.comments = new Map(Object.entries(response.data.comments || {}))
      this.auditLog = response.data.auditLog || []

      // Sync refreshed data to Yjs if collaboration is active
      if (this.commentsMap && this.ydoc) {
        this.ydoc.transact(() => {
          // Clear and repopulate to ensure consistency
          this.commentsMap?.clear()
          this.comments.forEach((comment, id) => {
            this.commentsMap?.set(id, comment)
          })
        }, this.ydoc)
      }
    }
    this.notifyListeners()
  }

  dispose(): void {
    // Clean up Yjs resources
    if (this.provider) {
      this.provider.disconnect()
      this.provider.destroy()
      this.provider = null
    }
    if (this.ydoc) {
      this.ydoc.destroy()
      this.ydoc = null
    }
    this.commentsMap = null

    this.ready = false
    this.comments.clear()
    this.auditLog = []
    this.listeners.clear()
    this.connectionListeners.clear()
    this.notifyConnectionListeners(false)
  }

  /**
   * Add comment - calls API first, syncs to Yjs, then refreshes
   */
  add(commentData: CreateCommentData): string {
    const id = crypto.randomUUID()
    const commentForApi: Omit<Comment, "createdAt"> = {
      id,
      projectId: commentData.projectId,
      variantName: commentData.variantName,
      authorId: commentData.authorId,
      authorName: commentData.authorName,
      content: commentData.content,
      status: commentData.status,
      originalText: commentData.originalText,
      lineNumber: commentData.lineNumber,
      replies: [],
      isDeleted: false,
    }

    // Sync to Yjs immediately for real-time broadcast
    this.syncToYjs(id, commentForApi as Comment)

    this.api
      .addCommentById(this.projectId, this.owner, commentForApi as Comment)
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to add comment:", error)
      })

    return id
  }

  /**
   * Update comment - syncs to Yjs, calls API, then refreshes
   */
  update(commentId: string, updates: Partial<Comment>): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    // Create updated comment for Yjs broadcast
    const updatedComment: Comment = {
      ...comment,
      ...updates,
      updatedAt: new Date().toISOString(),
    }
    this.syncToYjs(commentId, updatedComment)

    this.api
      .updateCommentById(this.projectId, this.owner, commentId, updates)
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to update comment:", error)
      })
  }

  /**
   * Delete comment - syncs to Yjs, calls API, then refreshes
   */
  delete(
    commentId: string,
    deletedBy: string,
    deletedByName: string,
    reason: Comment["deletionReason"]
  ): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    // Create deleted comment for Yjs broadcast (soft delete)
    const deletedComment: Comment = {
      ...comment,
      isDeleted: true,
      deletedAt: new Date().toISOString(),
      deletedBy,
      deletedByName,
      deletionReason: reason,
    }
    this.syncToYjs(commentId, deletedComment)

    this.api
      .deleteCommentById(this.projectId, this.owner, commentId, {
        deletedBy,
        deletedByName,
        reason: reason || "manual_delete",
      })
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to delete comment:", error)
      })
  }

  /**
   * Add reply - syncs to Yjs, calls API, then refreshes
   */
  addReply(commentId: string, replyData: CreateReplyData): string {
    const comment = this.comments.get(commentId)
    if (!comment) return ""

    const replyId = crypto.randomUUID()
    const replyForApi: Omit<CommentReply, "createdAt"> = {
      id: replyId,
      commentId: replyData.commentId,
      authorId: replyData.authorId,
      authorName: replyData.authorName,
      content: replyData.content,
    }

    // Create updated comment with new reply for Yjs broadcast
    const updatedComment: Comment = {
      ...comment,
      replies: [...comment.replies, replyForApi as CommentReply],
      updatedAt: new Date().toISOString(),
    }
    this.syncToYjs(commentId, updatedComment)

    this.api
      .addReplyById(
        this.projectId,
        this.owner,
        commentId,
        replyForApi as CommentReply
      )
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to add reply:", error)
      })

    return replyId
  }

  /**
   * Delete reply - syncs to Yjs, calls API, then refreshes
   */
  deleteReply(commentId: string, replyId: string): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    // Create updated comment without the reply for Yjs broadcast
    const updatedComment: Comment = {
      ...comment,
      replies: comment.replies.filter((r) => r.id !== replyId),
      updatedAt: new Date().toISOString(),
    }
    this.syncToYjs(commentId, updatedComment)

    this.api
      .deleteReplyById(this.projectId, this.owner, commentId, replyId)
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to delete reply:", error)
      })
  }

  /**
   * Set status - syncs to Yjs, calls API, then refreshes
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setStatus(commentId: string, status: CommentStatus, _userId: string, _userName: string): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    // Create updated comment for Yjs broadcast
    const updatedComment: Comment = {
      ...comment,
      status,
      updatedAt: new Date().toISOString(),
    }
    this.syncToYjs(commentId, updatedComment)

    this.api
      .updateCommentById(this.projectId, this.owner, commentId, { status })
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to update status:", error)
      })
  }

  /**
   * Resolve comment - syncs to Yjs, calls API, then refreshes
   */
  resolve(commentId: string, resolvedBy: string, resolvedByName: string): void {
    const comment = this.comments.get(commentId)
    if (!comment) return

    // Create resolved comment for Yjs broadcast
    const resolvedComment: Comment = {
      ...comment,
      status: "resolved",
      resolvedAt: new Date().toISOString(),
      resolvedBy,
      resolvedByName,
      updatedAt: new Date().toISOString(),
    }
    this.syncToYjs(commentId, resolvedComment)

    this.api
      .resolveCommentById(
        this.projectId,
        this.owner,
        commentId,
        resolvedBy,
        resolvedByName
      )
      .then(() => this.refreshFromBackend())
      .catch((error) => {
        console.error("Failed to resolve comment:", error)
      })
  }

  /**
   * Persist is a no-op - individual operations already sync to backend
   */
  protected persist(): void {
    // No-op
  }
}
